import type { SecurityKnowledgeUnit, RAGQueryResult, VectorSearchResult } from './ragTypes';
// Import from essentialExports for guaranteed compatibility
import { getRAGStore, getAPIKey, searchKnowledgeUnits } from './essentialExports';
import { QueryType } from './ragTypes';
import { HfInference } from "@huggingface/inference";

// Initialize Hugging Face client at the module level
const apiKey = getAPIKey();
const hf = apiKey ? new HfInference(apiKey) : null;

/**
 * Process a RAG query using OpenAI API or Hugging Face
 * @param query The user query
 * @param queryType The type of query (general, technique, payload, etc.)
 * @returns RAG query result
 */
export async function processRagQuery(
  query: string,
  queryType: QueryType = QueryType.GENERAL
): Promise<RAGQueryResult> {
  try {
    console.log('RAG AI: Processing query:', query);
    console.log('RAG AI: Query type:', queryType);

    // Get API key (ensure it's still being retrieved in case of dynamic key management)
    const currentApiKey = getAPIKey();
    if (!currentApiKey) {
      throw new Error("API key is required for RAG queries");
    }

    // Get relevant knowledge units
    let relevantUnits = searchKnowledgeUnits(query);
    console.log(`RAG AI: Found ${relevantUnits.length} relevant knowledge units`);

    if (relevantUnits.length === 0) {
      return {
        answer: "I couldn't find specific information related to your query in the knowledge base. Please try rephrasing your question or using more specific keywords.",
        relevantUnits: [],
        confidence: 0,
        sources: []
      };
    }

    // Filter and limit the results to improve relevance
    relevantUnits = filterRelevantUnits(relevantUnits, query, queryType);
    console.log(`RAG AI: After filtering, using ${relevantUnits.length} most relevant knowledge units`);

    // Check if this is a specialized query that should use Hugging Face
    const shouldUseHuggingFace = shouldUseAdvancedGeneration(query, queryType);
    console.log('RAG AI: Using advanced generation:', shouldUseHuggingFace);

    if (shouldUseHuggingFace) {
      return await processHuggingFaceQuery(query, relevantUnits, queryType, currentApiKey);
    }

    // For other queries, use the standard response formatting
    const formattedResponse = formatResponse(query, relevantUnits, queryType);

    return {
      answer: formattedResponse,
      relevantUnits: relevantUnits,
      confidence: 0.85,
      sources: relevantUnits.map(unit => ({
        title: unit.title,
        sku_id: unit.sku_id
      }))
    };
  } catch (error) {
    console.error("Error processing RAG query:", error);
    throw error;
  }
}

/**
 * Detect the query type based on the query content
 */
function detectQueryType(query: string): QueryType {
  const lowerQuery = query.toLowerCase();

  if (lowerQuery.includes('tool') || lowerQuery.includes('scanner') ||
      lowerQuery.includes('program') || lowerQuery.includes('software') ||
      lowerQuery.includes('utility') || lowerQuery.includes('scan with')) {
    return QueryType.TOOL;
  }

  if (lowerQuery.includes('payload') || lowerQuery.includes('injection string') ||
      lowerQuery.includes('exploit code') || lowerQuery.includes('attack string')) {
    return QueryType.PAYLOAD;
  }

  if (lowerQuery.includes('bypass') || lowerQuery.includes('waf') ||
      lowerQuery.includes('evade') || lowerQuery.includes('circumvent')) {
    return QueryType.WAF_BYPASS;
  }

  if (lowerQuery.includes('technique') || lowerQuery.includes('method') ||
      lowerQuery.includes('approach') || lowerQuery.includes('how to')) {
    return QueryType.TECHNIQUE;
  }

  return QueryType.GENERAL;
}

/**
 * Filter and prioritize knowledge units based on query relevance
 * @param units All retrieved knowledge units
 * @param query The user query
 * @param queryType The type of query
 * @returns Filtered and prioritized knowledge units
 */
function filterRelevantUnits(
  units: SecurityKnowledgeUnit[],
  query: string,
  queryType: QueryType
): SecurityKnowledgeUnit[] {
  // Extract important keywords from the query
  const queryLower = query.toLowerCase();
  const stopWords = ['what', 'which', 'when', 'where', 'how', 'can', 'should', 'would', 'the', 'and', 'for', 'me', 'i', 'you'];
  const keywords = queryLower.split(/\s+/).filter(word =>
    word.length > 2 &&
    !stopWords.includes(word)
  );

  // Score each unit based on relevance to the query
  const scoredUnits = units.map(unit => {
    let score = 0;
    const titleLower = (unit.title || '').toLowerCase();
    const descLower = (unit.description || '').toLowerCase();

    // Title exact match is a strong signal
    if (titleLower.includes(queryLower)) {
      score += 100;
    }

    // Count keywords in title (weighted higher)
    keywords.forEach(keyword => {
      if (titleLower.includes(keyword)) {
        score += 20;
      }
      if (descLower.includes(keyword)) {
        score += 10;
      }
    });

    // Prioritize by query type match
    if (queryType === QueryType.TOOL && unit.tools && unit.tools.length > 0) {
      score += 70;
    } else if (queryType === QueryType.PAYLOAD && unit.payload_examples && unit.payload_examples.length > 0) {
      score += 70;
    } else if (queryType === QueryType.TECHNIQUE && unit.techniques && unit.techniques.length > 0) {
      score += 70;
    } else if (queryType === QueryType.WAF_BYPASS && unit.waf_bypasses && unit.waf_bypasses.length > 0) {
      score += 70;
    }

    return { unit, score };
  });

  // Sort by score (highest first)
  const sortedUnits = scoredUnits.sort((a, b) => b.score - a.score);

  // Take top N results to keep responses focused
  const MAX_UNITS = 3; // Limit to 3 most relevant units
  return sortedUnits.slice(0, MAX_UNITS).map(item => item.unit);
}

/**
 * Determine if a query should use advanced generation
 */
function shouldUseAdvancedGeneration(query: string, queryType: QueryType): boolean {
  // Make this function always return true if you want all queries to use advanced generation
  // For now, we'll keep some basic logic to determine if advanced generation is needed
  const queryLower = query.toLowerCase();

  // Check for any security-related terms that might benefit from advanced generation
  const securityTerms = ['xss', 'csrf', 'sql', 'injection', 'bypass', 'waf', 'payload',
                            'filter', 'evasion', 'attack', 'exploit', 'vulnerability'];

  const hasSecurityTerm = securityTerms.some(term => queryLower.includes(term));

  // If it's a payload query or mentions security terms, use advanced generation
  return queryType === QueryType.PAYLOAD || hasSecurityTerm;
}

/**
 * Process query using Hugging Face Inference API
 */
async function processHuggingFaceQuery(
  query: string,
  relevantUnits: SecurityKnowledgeUnit[],
  queryType: QueryType,
  currentApiKey: string // Pass the API key
): Promise<RAGQueryResult> {
  try {
    console.log('RAG AI: Processing with Hugging Face');

    if (!hf) {
      throw new Error("Hugging Face client could not be initialized.");
    }

    // Extract examples from relevant units for context - limit to avoid response bloat
    const examples = relevantUnits
      .flatMap(unit => {
        const payloads = unit.payload_examples || [];
        const codeSamples = unit.code_samples || [];
        return [...payloads, ...codeSamples];
      })
      .filter(example => example)
      .slice(0, 4); // Reduced from 8 to 4 examples max

    console.log(`RAG AI: Using ${examples.length} examples for context`);

    // Create a more focused prompt with context from the knowledge base
    const promptWithContext = `
You are a cybersecurity expert specializing in web security.
Generate a specific and concise response to the following user query:
"${query}"

Here is the most relevant information from our security knowledge base:
${relevantUnits.slice(0, 3).map(unit => `- ${unit.title}: ${unit.description || ''}`).join('\n')}

${examples.length > 0 ? 'Here are some relevant examples:\n' +
  examples.map(example => {
    if (typeof example === 'string') return `- ${example}`;
    if (example && typeof example === 'object' && example.code)
      return `- ${example.code}`;
    return '';
  }).filter(Boolean).join('\n') : ''}

Important: Be direct and specific to the query. Do not list irrelevant information.
Format your response in a clear, structured way with sections for:
1. SUMMARY (1-2 sentences about ${query})
2. DETAILED EXPLANATION (focused only on ${query})
3. EXAMPLES (if relevant)
4. CONCLUSION with concise recommendations
`;

    console.log('RAG AI: Sending request to Hugging Face');

    // Call the Hugging Face API using the pre-initialized client
    const chatCompletion = await hf.chatCompletion({
      model: "Qwen/Qwen3-235B-A22B", // Using Qwen model but can be changed
      messages: [
        {
          role: "system",
          content: "You are a cybersecurity expert specializing in web security. Provide focused, specific answers only to the exact query. Avoid listing irrelevant information."
        },
        {
          role: "user",
          content: promptWithContext
        }
      ],
      provider: "novita",
      temperature: 0.2, // Reduced for more factual responses
      max_tokens: 800, // Reduced to encourage conciseness
      top_p: 0.85,
    });

    // Extract the response
    const generatedResponse = chatCompletion.choices[0]?.message?.content?.trim() || "No response generated";

    console.log('RAG AI: Received response from Hugging Face:', generatedResponse.substring(0, 50) + '...');

    // Format the final response if it doesn't already have proper formatting
    let finalResponse = generatedResponse;
    if (!generatedResponse.includes("SUMMARY:") && !generatedResponse.includes("1.")) {
      finalResponse = formatGeneratedResponse(generatedResponse, query, queryType);
    }

    return {
      answer: finalResponse,
      relevantUnits: relevantUnits,
      confidence: 0.9,
      sources: relevantUnits.map(unit => ({
        title: unit.title,
        sku_id: unit.sku_id
      }))
    };
  } catch (error) {
    console.error("Error processing Hugging Face query:", error);

    // Fallback to standard response if Hugging Face fails
    const formattedResponse = formatResponse(query, relevantUnits, queryType);

    return {
      answer: formattedResponse,
      relevantUnits: relevantUnits,
      confidence: 0.9,
      sources: relevantUnits.map(unit => ({
        title: unit.title,
        sku_id: unit.sku_id
      }))
    };
  }
}

/**
 * Format a generated response to ensure it has the proper structure
 */
function formatGeneratedResponse(
  response: string,
  query: string,
  queryType: QueryType
): string {
  // Extract what appears to be the main topic
  let topic = query.split(' ').slice(0, 3).join(' ');
  if (response.split('\n')[0] && response.split('\n')[0].length < 100) {
    topic = response.split('\n')[0];
  }

  return `
1. SUMMARY: ${topic}

2. DETAILED EXPLANATION:
${response}

3. CONCLUSION:
    Always ensure you have proper authorization before testing security techniques. Follow responsible disclosure practices if you discover vulnerabilities.
`;
}

/**
 * Format response based on query type and relevant units
 */
function formatResponse(
  query: string,
  relevantUnits: SecurityKnowledgeUnit[],
  queryType: QueryType
): string {
  // Safely get string value
  const safeString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };

  if (relevantUnits.length === 0) {
    return "I couldn't find specific information related to your query in the knowledge base.";
  }

  const mainUnit = relevantUnits[0];

  switch (queryType) {
    case QueryType.PAYLOAD:
      // Format payload response
      const payloadExamples = relevantUnits.flatMap(unit =>
        unit.payload_examples && Array.isArray(unit.payload_examples) ?
        unit.payload_examples.map(payload => safeString(payload)).filter(p => p.trim() !== '') :
        []
      ).slice(0, 5); // Limit to 5 examples

      const codeSamples = relevantUnits.flatMap(unit =>
        unit.code_samples && Array.isArray(unit.code_samples) ?
        unit.code_samples.filter(sample => sample && typeof sample === 'object') :
        []
      ).slice(0, 3); // Limit to 3 code samples

      return `
1. SUMMARY: ${safeString(mainUnit.title)} - Payload Examples

2. DETAILED EXPLANATION:
    ${relevantUnits.slice(0, 3).map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n    ')}

3. EXAMPLE(S):
    ${payloadExamples.length > 0 ?
      payloadExamples.map(payload => `\`\`\`\n${payload}\n\`\`\``).join('\n\n    ') :
      'No specific payload examples available in the knowledge base.'}

    ${codeSamples.length > 0 ?
      codeSamples.map(sample => `\`\`\`${safeString(sample.language)}\n${safeString(sample.code)}\n\`\`\``).join('\n\n    ') :
      ''}

4. CONCLUSION:
    When testing for ${safeString(mainUnit.title)}, always ensure you have proper authorization and follow responsible disclosure practices.
`;

    case QueryType.TECHNIQUE:
      // Format technique response
      return `
1. SUMMARY: ${safeString(mainUnit.title)} - Technical approach for ${safeString(mainUnit.target_component || 'security testing')}

2. DETAILED EXPLANATION:
    ${relevantUnits.slice(0, 3).map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n    ')}

    ${mainUnit.techniques && Array.isArray(mainUnit.techniques) && mainUnit.techniques.length > 0 ?
      `Key techniques include: ${mainUnit.techniques.slice(0, 5).map(t => `*${safeString(t)}*`).join(', ')}` : ''}

    ${mainUnit.severity ? `Severity: ${safeString(mainUnit.severity)}, Difficulty: ${safeString(mainUnit.difficulty || 'Moderate')}` : ''}

3. EXAMPLE(S):
    ${formatExamples(relevantUnits)}

4. CONCLUSION:
    These techniques are part of the ${safeString(mainUnit.methodology || 'security assessment')} methodology and are typically used during the ${safeString(mainUnit.step || 'testing')} phase.
`;

    case QueryType.TOOL:
      // Format tool response
      // Get all tools from all relevant units
      const allTools = relevantUnits.flatMap(unit =>
        unit.tools && Array.isArray(unit.tools) ? unit.tools : []
      );

      // Remove duplicate tools based on name
      const uniqueTools = allTools.filter((tool, index) => {
        const toolName = safeString(tool.name).toLowerCase();
        return allTools.findIndex(t => safeString(t.name).toLowerCase() === toolName) === index;
      }).slice(0, 5); // Limit to 5 tools

      return `
1. SUMMARY: Recommended tools for ${safeString(mainUnit.title)}

2. DETAILED EXPLANATION:
    ${relevantUnits.slice(0, 3).map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n    ')}

3. RECOMMENDED TOOLS:
    ${uniqueTools.length > 0 ?
      uniqueTools.map(tool => `- **${safeString(tool.name)}**: ${tool.url ? safeString(tool.url) : 'No URL provided'}`).join('\n    ') :
      'No specific tools found in the knowledge base for this query.'}

4. CONCLUSION:
    These tools can help you effectively test for and identify ${safeString(mainUnit.title.toLowerCase())} vulnerabilities. Always use security tools responsibly and legally.
`;

    case QueryType.WAF_BYPASS:
      // Format WAF bypass response
      return `
1. SUMMARY: WAF bypass techniques for ${safeString(mainUnit.title)}

2. DETAILED EXPLANATION:
    ${relevantUnits.slice(0, 3).map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n    ')}

3. BYPASS TECHNIQUES:
    ${relevantUnits.flatMap(unit =>
      unit.waf_bypasses && Array.isArray(unit.waf_bypasses) && unit.waf_bypasses.length > 0 ?
      unit.waf_bypasses.slice(0, 5).map(bypass => `- ${safeString(bypass)}`) :
      []
    ).join('\n    ') || '- No specific WAF bypasses found in the knowledge base for this query.'}

4. CONCLUSION:
    When testing WAF bypasses, always ensure you have proper authorization and follow responsible disclosure practices.
`;

    default:
      // Format general response
      return `
1. SUMMARY: ${safeString(mainUnit.title)} - ${safeString(mainUnit.description)}

2. DETAILED EXPLANATION:
    ${relevantUnits.slice(0, 3).map((unit, i) => `- ${safeString(unit.title)}: ${safeString(unit.description)}`).join('\n    ')}

    ${mainUnit.techniques && Array.isArray(mainUnit.techniques) && mainUnit.techniques.length > 0 ?
      `Common techniques include: ${mainUnit.techniques.slice(0, 3).map(t => `*${safeString(t)}*`).join(', ')}` : ''}

    ${mainUnit.tools && Array.isArray(mainUnit.tools) && mainUnit.tools.length > 0 ?
      `Recommended tools: ${mainUnit.tools.slice(0, 3).map(t => `**${safeString(t.name)}**`).join(', ')}` : ''}

3. KEY POINTS:
    - ${safeString(mainUnit.title)} is an important security consideration.
    ${mainUnit.severity ? `- Severity: ${safeString(mainUnit.severity)}` : ''}
    ${mainUnit.difficulty ? `- Difficulty: ${safeString(mainUnit.difficulty)}` : ''}
    - Follow security best practices when testing.

4. CONCLUSION:
    This information is sourced from the RAWPA security knowledge base. Always ensure you have proper authorization before testing any security techniques.
`;
  }
}

/**
 * Format examples from knowledge units
 */
function formatExamples(units: SecurityKnowledgeUnit[]): string {
  // Safely get string value
  const safeString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return String(value);
  };

  // Get payload examples
  const payloads = units.flatMap(unit =>
    unit.payload_examples && Array.isArray(unit.payload_examples) ?
    unit.payload_examples.map(payload => `\`\`\`\n${safeString(payload)}\n\`\`\``) :
    []
  );

  // Get code samples
  const codeSamples = units.flatMap(unit =>
    unit.code_samples && Array.isArray(unit.code_samples) ?
    unit.code_samples.filter(sample => sample && typeof sample === 'object')
      .map(sample => `\`\`\`${safeString(sample.language)}\n${safeString(sample.code)}\n\`\`\``) :
    []
  );

  // Limit examples to avoid overwhelming responses
  const allExamples = [...payloads, ...codeSamples];
  const limitedExamples = allExamples.slice(0, 3).join('\n\n    ');

  return limitedExamples || 'No specific examples available in the knowledge base.';
}